import wsClient from "src/wsClient"

/* The seat a delta is about, or nothing (B4).

   Every delta below used to dereference this find() unguarded, so a message about a player the
   client had not heard of — a delta that beat the snapshot it belongs to, or one for somebody who
   has just left — crashed the reducer instead of being ignored. It is safe to ignore: the whole
   lobby follows every action now, and that is what the client trusts. */
const seatOf = (state, userId) =>
  state.lobbies[0]?.lobbyUsers.find((lobbyUser) => lobbyUser.user.id === userId)

/* The whole lobby, from the REST snapshot or from a `lobby_refreshed` broadcast (B4).

   `{ lobbies, ifNewer: true }` is the socket's form: a payload whose `version` is *lower* than
   the one already held is dropped. Two consumers that serialise after two commits can broadcast
   in either order, and the loser of that race is a payload describing a room that no longer
   exists — a seat somebody has just taken, gone again. Equal applies (it is the same room, and
   re-applying it changes nothing), and the REST snapshot always applies because it is what a
   client boots from. */
const setLobbies = (state, action) => {
  const fromSocket = !Array.isArray(action.payload)
  const lobbies = fromSocket ? action.payload.lobbies : action.payload
  const isStale = (lobby) => {
    const held = state.lobbies.find((existing) => existing.number === lobby.number)
    return held && (lobby.version ?? 0) < (held.version ?? 0)
  }
  if (fromSocket && action.payload.ifNewer && lobbies.length > 0 && lobbies.every(isStale)) {
    return state
  }
  // A different room than the one held: what was said in the last one is not this room's.
  const incomingNumber = lobbies[0]?.number
  if (state.lobbies[0]?.number !== incomingNumber) state.chatMessages = []
  state.lobbies = lobbies.map((lobby) => ({
    number: lobby.number,
    host: {
      id: lobby.host.id,
      username: lobby.host.username,
      rankingPoints: lobby.host.ranking_points,
      league: lobby.host.league ?? null,
    },
    mode: lobby.mode,
    // B1: which snapshot of the room this is, so a later payload can never be overwritten by an
    // earlier one. A server from before it published one counts as the oldest there is.
    version: lobby.version ?? 0,
    // The host's clock for this game, visible to everyone (2026-08-21).
    roundSeconds: lobby.round_seconds,
    // Whether the game it starts moves the ranking (owner, 2026-10-04); set when the room is made.
    ranked: lobby.ranked !== false,
    // Which world the room plays on (requirement 35): "S" | "M" | "L", set when the room is made
    // and never changed. A server from before the sizes has only the one world, L.
    mapSize: lobby.map_size ?? "L",
    // How good every bot of survival's endless side is (SV3). Null on a classic lobby, which is
    // what the enemy column keys on as much as the mode does. It is set over the socket and the
    // server answers with the whole lobby, so this mapping is the only place it has to cross.
    survivalDifficulty: lobby.survival_difficulty ?? null,
    // The user ids of seats the search queue gave whose players have not arrived yet (owner,
    // 2026-10-07): while there are any, the lobby view asks the server to check on them.
    awaiting: lobby.awaiting ?? [],
    membersPerTeam: lobby.members_per_team,
    lobbyUsers: lobby.lobby_users.map((lobbyUser) => ({
      user: {
        id: lobbyUser.user.id,
        username: lobbyUser.user.username,
        rankingPoints: lobbyUser.user.ranking_points,
        // The league the rating reads as (owner, 2026-10-08), the server's: it frames the icon.
        league: lobbyUser.user.league ?? null,
        // What the player wears, the picture included: without it every other seat drew the
        // default face (owner, 2026-10-09). Resolved by the server, so it is always drawable.
        equippedCosmetics: lobbyUser.user.equipped_cosmetics ?? null,
        // Bots only ever arrive through lobby_refreshed (add_bot answers with the whole lobby),
        // so this mapping is the one place the flag has to cross.
        isBot: lobbyUser.user.is_bot,
      },
      team: lobbyUser.team,
      race: lobbyUser.race,
      // The rung this seat plays at (requirement 31, 2026-09-20), beside the class for the same
      // reason: both are the host's per-seat choice, and the account is an empty body with a name.
      // The `user` fallback is the cutover only — a server still on the old shape keeps its
      // plates, and this reducer is the one place in the client that knows the old address.
      botDifficulty: lobbyUser.bot_difficulty ?? lobbyUser.user?.bot_difficulty ?? null,
      // The colour this seat plays as — see `Lobby.colour_clashes` on the server.
      unitIcon: lobbyUser.unit_icon,
      locations: lobbyUser.locations,
      ready: lobbyUser.ready,
      // Whether the member has the room open (gamekit 0.8.0): away is still in the room. A
      // server that does not say is read as here.
      connected: lobbyUser.connected ?? true,
    })),
  }))
  return state
}

const leaveLobby = (state, action) => {
  const { lobbyNumber, sentHome = false } = action.payload
  state.lobbies = state.lobbies.filter((lobby) => lobby.number !== lobbyNumber)
  // The room is over for this client (left, kicked, or it became a game): so is its talk.
  state.chatMessages = []
  /* Kicked, or the seat given up from another tab: nobody else navigates, so the lobby route
     must take the player home (owner, 2026-10-16: a black screen at the room's address, and a
     reload joined the room again). A LEAVE or a game start navigates by itself and leaves this off. */
  state.sentHome = Boolean(sentHome)
  wsClient.close("lobby")
  return state
}

const arrivedHome = (state) => {
  state.sentHome = false
  return state
}

const addChatMessage = (state, action) => {
  state.chatMessages.push(action.payload)
  return state
}

/* Somebody arrived. A no-op when that seat is already here, which is the "she was seeing 2
   menyanyos" bug (owner, 2026-09-20): the REST snapshot a client boots from is read before its
   socket subscribes, so a `user_joined_lobby` for somebody already in that snapshot arrived
   second and was pushed again. A delta has to be safe to apply twice, because the snapshot that
   follows it is what is trusted. */
const addUserToLobby = (state, action) => {
  const lobby = state.lobbies[0]
  if (!lobby) return state
  if (lobby.lobbyUsers.some((lobbyUser) => lobbyUser.user.id === action.payload.user.id)) return state
  lobby.lobbyUsers.push({
    user: action.payload.user,
    team: null,
    race: null,
    locations: [],
    ready: false,
    connected: true,
  })
  return state
}

const removeUserFromLobby = (state, action) => {
  const { userId, host } = action.payload
  const lobby = state.lobbies[0]
  if (!lobby) return state
  lobby.lobbyUsers = lobby.lobbyUsers.filter((lobbyUser) => lobbyUser.user.id !== userId)
  lobby.host = host
  return state
}

const addUserToTeam = (state, action) => {
  const { userId, team, unitIcon } = action.payload
  const lobby = state.lobbies[0]
  if (!lobby) return state
  const lobbyUsers = lobby.lobbyUsers
  lobbyUsers.forEach((lobbyUser) => {
    if (lobbyUser.user.id === userId) {
      lobbyUser.team = team
      lobbyUser.race = null
      lobbyUser.locations = []
      lobbyUser.ready = false
      // The server picks a free colour on sitting down and the broadcast carries it, so the seat
      // arrives wearing one — nothing here has to wait for a whole-lobby refresh to know.
      lobbyUser.unitIcon = unitIcon ?? null
    }
  })
  return state
}

const removeUserFromTeam = (state, action) => {
  const { userId } = action.payload
  const lobby = state.lobbies[0]
  if (!lobby) return state
  const lobbyUsers = lobby.lobbyUsers
  lobbyUsers.forEach((lobbyUser) => {
    if (lobbyUser.user.id === userId) {
      lobbyUser.team = null
      lobbyUser.race = null
      lobbyUser.locations = []
      lobbyUser.ready = false
    }
  })
  return state
}

/* A seat took a colour. Kept in step here rather than waiting for the next whole-lobby payload,
   because the *other* players' pickers need it: a swatch somebody has just taken must grey out
   before somebody else presses it and gets a refusal. */
const changeUserUnitIcon = (state, action) => {
  const lobbyUser = seatOf(state, action.payload.userId)
  if (lobbyUser) lobbyUser.unitIcon = action.payload.unitIcon
  return state
}

const changeUserRace = (state, action) => {
  const lobbyUser = seatOf(state, action.payload.userId)
  if (lobbyUser) lobbyUser.race = action.payload.race
  return state
}

const switchUserLocation = (state, action) => {
  const { userId, location } = action.payload
  const lobbyUser = seatOf(state, userId)
  if (!lobbyUser) return state

  if (lobbyUser.locations.some((l) => l.row === location.row && l.column === location.column)) {
    lobbyUser.locations = lobbyUser.locations.filter((l) => !(l.row === location.row && l.column === location.column))
  } else {
    lobbyUser.locations.push(location)
  }
  return state
}

const setUserReady = (state, action) => {
  const lobbyUser = seatOf(state, action.payload.userId)
  if (lobbyUser) lobbyUser.ready = true
  return state
}

const unsetUserReady = (state, action) => {
  const lobbyUser = seatOf(state, action.payload.userId)
  if (lobbyUser) lobbyUser.ready = false
  return state
}


// The host changed the round's length; everyone's lobby follows.
const setRoundSeconds = (state, action) => {
  if (state.activeLobby) state.activeLobby.roundSeconds = action.payload
  return state
}

const reducers = {
  setRoundSeconds,
  setLobbies,
  addChatMessage,
  addUserToTeam,
  removeUserFromTeam,
  changeUserRace,
  changeUserUnitIcon,
  leaveLobby,
  arrivedHome,
  addUserToLobby,
  removeUserFromLobby,
  switchUserLocation,
  setUserReady,
  unsetUserReady,
}

export default reducers
